import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Target, AlertTriangle, ChevronDown, ChevronUp, ChevronRight,
  CheckCircle2, Loader2, FileText, Zap, Brain,
} from 'lucide-react';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type {
  StrategicPhase, StrategicDeliverable, StressTestResult, GhostDeckSlide,
} from '@/hooks/useStrategicStream';

export function StrategicDeliverableCard({ phase, deliverable, stressTestResults, ghostDeck, isSmallScreen }: {
  phase: StrategicPhase;
  deliverable: StrategicDeliverable | null;
  stressTestResults: StressTestResult[];
  ghostDeck?: GhostDeckSlide[];
  isSmallScreen: boolean;
}) {
  const isActive = phase === 'aggregation';
  const isComplete = phase === 'concluded';
  const [isExpanded, setIsExpanded] = useState(true);

  const killSheet = deliverable?.killSheet;
  const hasContent = isComplete && (killSheet || deliverable?.executiveSummary);

  const headwinds: string[] = [];
  const tailwinds: string[] = [];

  if (killSheet?.risks) headwinds.push(...killSheet.risks);
  if (killSheet?.opportunities) tailwinds.push(...killSheet.opportunities);

  stressTestResults.forEach(result => {
    if (result.testResult === 'invalidated' || result.confidence < 0.5) {
      headwinds.push(`Assumption challenged: ${result.assumptionStatement} (${Math.round(result.confidence * 100)}% confidence)`);
    } else if (result.testResult === 'validated' && result.confidence >= 0.7) {
      tailwinds.push(`Validated: ${result.assumptionStatement} (${Math.round(result.confidence * 100)}% confidence)`);
    }
  });

  const assumptions = stressTestResults.map(result => ({
    statement: result.assumptionStatement,
    status: result.testResult,
    confidence: result.confidence,
    evidenceFor: result.evidenceFor,
    evidenceAgainst: result.evidenceAgainst,
  }));

  return (
    <div className={cn('mx-auto rounded-2xl border-2 backdrop-blur transition-all duration-300',
      isActive && 'animate-pulse border-verdict-primary/60 shadow-lg shadow-verdict-primary/20',
      isComplete ? 'border-verdict-primary/50 bg-gradient-to-br from-verdict-primary/15 to-amber-500/5' : 'border-verdict-primary/30 bg-verdict-primary/5',
      isSmallScreen ? 'max-w-[340px] p-4' : 'max-w-[900px] p-6')}>

      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full">
        <div className={cn('flex items-center justify-center gap-2 mb-2 text-verdict-primary font-semibold uppercase', isSmallScreen ? 'text-sm' : 'text-lg')}>
          {isComplete ? <CheckCircle2 className="w-6 h-6" /> : isActive ? <Loader2 className="w-6 h-6 animate-spin" /> : <FileText className="w-6 h-6" />}
          <span>{isComplete ? 'Strategic Analysis Complete' : 'Final Aggregator'}</span>
          {hasContent && (isExpanded ? <ChevronUp className="w-5 h-5 text-text-secondary" /> : <ChevronDown className="w-5 h-5 text-text-secondary" />)}
        </div>
      </button>
      <div className={cn('text-text-secondary text-center mb-5', isSmallScreen ? 'text-sm' : 'text-base')}>
        {isComplete ? 'Comprehensive strategic analysis with actionable insights' : 'Compiling strategic deliverable with ghost deck and decision brief'}
      </div>

      {/* Status Indicators */}
      <div className="flex gap-3 justify-center flex-wrap mb-5">
        {killSheet && (
          <span className={cn('px-4 py-2 rounded-lg bg-verdict-primary/10 border border-verdict-primary/30 text-verdict-primary flex items-center gap-2 font-medium', isSmallScreen ? 'text-sm' : 'text-base')}>
            <CheckCircle2 className="w-5 h-5" /> Decision Brief Ready
          </span>
        )}
      </div>

      {/* Ghost Deck Streaming Preview */}
      {ghostDeck && ghostDeck.length > 0 && isActive && (
        <div className="mb-5">
          <div className={cn('text-verdict-primary/80 mb-2 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
            <FileText className="w-4 h-4" />
            Ghost Deck ({ghostDeck.length} slides generating...)
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-verdict-primary/20 scrollbar-track-transparent">
            {ghostDeck.map((slide, i) => (
              <div key={i} className="flex-shrink-0 w-[120px] rounded-lg bg-surface-light/50 border border-border/30 p-2">
                <div className="text-[10px] text-text-muted font-mono mb-1">Slide {slide.slideNumber || i + 1}</div>
                <div className="text-xs text-text-primary font-medium truncate">{slide.title}</div>
                <div className="text-[10px] text-text-muted mt-1 truncate">{slide.slideType}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isExpanded && (
        <div className="space-y-5">
          {/* Recommendation FIRST (pyramid principle) */}
          {killSheet?.recommendation && (
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-5">
              <div className={cn('text-blue-400/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
                <Target className="w-4 h-4" />
                Strategic Recommendation
                {killSheet.confidenceLevel && (
                  <span className="ml-2 px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
                    {killSheet.confidenceLevel}
                  </span>
                )}
              </div>
              <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-sm' : 'text-base')}>
                <MarkdownContent content={killSheet.recommendation} className={isSmallScreen ? 'text-sm' : 'text-base'} />
              </div>
            </div>
          )}

          {/* Next Steps (immediately after recommendation) */}
          {killSheet?.nextSteps && killSheet.nextSteps.length > 0 && (
            <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-5">
              <div className={cn('text-purple-400/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
                <ChevronRight className="w-4 h-4" />
                Recommended Next Steps
              </div>
              <ol className={cn('space-y-2.5', isSmallScreen ? 'text-xs' : 'text-sm')}>
                {killSheet.nextSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-text-primary">
                    <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </span>
                    <MarkdownContent content={step} className={cn('flex-1 [&_p]:inline', isSmallScreen ? 'text-xs' : 'text-sm')} inline />
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Divider between "What We Recommend" and "What We Found" */}
          {(killSheet?.recommendation || (killSheet?.nextSteps && killSheet.nextSteps.length > 0)) &&
           (killSheet?.executiveSummary || deliverable?.executiveSummary || (killSheet?.keyFindings && killSheet.keyFindings.length > 0)) && (
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-surface-raised/50" />
              <span className="text-xs text-text-muted uppercase tracking-wider font-medium">Supporting Analysis</span>
              <div className="flex-1 h-px bg-surface-raised/50" />
            </div>
          )}

          {/* Executive Summary */}
          {(killSheet?.executiveSummary || deliverable?.executiveSummary) && (
            <div className="rounded-lg bg-gradient-to-r from-verdict-primary/20 to-amber-500/10 border border-verdict-primary/30 p-5">
              <div className={cn('text-verdict-primary/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
                <FileText className="w-4 h-4" />
                Executive Summary
              </div>
              <div className={cn('text-text-primary leading-relaxed', isSmallScreen ? 'text-sm' : 'text-base')}>
                <MarkdownContent content={killSheet?.executiveSummary || deliverable?.executiveSummary || ''} className={isSmallScreen ? 'text-sm' : 'text-base'} />
              </div>
            </div>
          )}

          {/* Key Findings */}
          {killSheet?.keyFindings && killSheet.keyFindings.length > 0 && (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-5">
              <div className={cn('text-emerald-400/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
                <CheckCircle2 className="w-4 h-4" />
                Key Findings
                <span className="ml-1 px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-xs">
                  {killSheet.keyFindings.length}
                </span>
              </div>
              <div className="max-h-[220px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-500/20 scrollbar-track-transparent">
                <ul className={cn('space-y-2.5', isSmallScreen ? 'text-xs' : 'text-sm')}>
                  {killSheet.keyFindings.map((finding, i) => (
                    <li key={i} className="flex items-start gap-2 text-text-primary">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <MarkdownContent content={finding} className={cn('flex-1 [&_p]:inline', isSmallScreen ? 'text-xs' : 'text-sm')} inline />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Headwinds vs Tailwinds */}
          {(headwinds.length > 0 || tailwinds.length > 0) && (
            <div className={cn('grid gap-4', isSmallScreen ? 'grid-cols-1' : 'grid-cols-2')}>
              <div className="rounded-lg bg-gradient-to-br from-red-500/15 to-orange-500/10 border border-red-500/30 p-5">
                <div className={cn('text-red-400 mb-3 flex items-center gap-2 uppercase tracking-wide font-semibold', isSmallScreen ? 'text-xs' : 'text-sm')}>
                  <AlertTriangle className="w-5 h-5" />
                  Headwinds
                  <span className="ml-auto px-2 py-0.5 rounded bg-red-500/20 text-red-300 text-xs font-normal">{headwinds.length}</span>
                </div>
                <div className="max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-red-500/20 scrollbar-track-transparent">
                  {headwinds.length > 0 ? (
                    <ul className={cn('space-y-2.5', isSmallScreen ? 'text-xs' : 'text-sm')}>
                      {headwinds.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-text-primary">
                          <span className="w-2 h-2 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                          <MarkdownContent content={item} className={cn('flex-1 [&_p]:inline', isSmallScreen ? 'text-xs' : 'text-sm')} inline />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-text-muted text-center py-2 text-sm">No significant headwinds identified</div>
                  )}
                </div>
              </div>
              <div className="rounded-lg bg-gradient-to-br from-green-500/15 to-emerald-500/10 border border-green-500/30 p-5">
                <div className={cn('text-green-400 mb-3 flex items-center gap-2 uppercase tracking-wide font-semibold', isSmallScreen ? 'text-xs' : 'text-sm')}>
                  <Zap className="w-5 h-5" />
                  Tailwinds
                  <span className="ml-auto px-2 py-0.5 rounded bg-green-500/20 text-green-300 text-xs font-normal">{tailwinds.length}</span>
                </div>
                <div className="max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-green-500/20 scrollbar-track-transparent">
                  {tailwinds.length > 0 ? (
                    <ul className={cn('space-y-2.5', isSmallScreen ? 'text-xs' : 'text-sm')}>
                      {tailwinds.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-text-primary">
                          <span className="w-2 h-2 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                          <MarkdownContent content={item} className={cn('flex-1 [&_p]:inline', isSmallScreen ? 'text-xs' : 'text-sm')} inline />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-text-muted text-center py-2 text-sm">No significant tailwinds identified</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Assumptions */}
          {assumptions.length > 0 && (
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-5">
              <div className={cn('text-amber-400/80 mb-3 flex items-center gap-2 uppercase tracking-wide font-medium', isSmallScreen ? 'text-xs' : 'text-sm')}>
                <Brain className="w-4 h-4" />
                Key Assumptions (Stress Tested)
                <span className="ml-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-xs">{assumptions.length}</span>
              </div>
              <div className="max-h-[220px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-amber-500/20 scrollbar-track-transparent space-y-3">
                {assumptions.map((assumption, i) => (
                  <div key={i} className="rounded-lg bg-surface-light/50 border border-border/50 p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className={cn('text-text-primary flex-1', isSmallScreen ? 'text-xs' : 'text-sm')}>
                        <MarkdownContent content={assumption.statement} className={isSmallScreen ? 'text-xs' : 'text-sm'} />
                      </div>
                      <span className={cn(
                        'px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0',
                        assumption.status === 'validated' ? 'bg-green-500/20 text-green-400' :
                        assumption.status === 'invalidated' ? 'bg-red-500/20 text-red-400' :
                        'bg-amber-500/20 text-amber-400'
                      )}>
                        {assumption.status === 'validated' ? 'Validated' : assumption.status === 'invalidated' ? 'Challenged' : 'Uncertain'}
                        {' '}{Math.round(assumption.confidence * 100)}%
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      {assumption.evidenceFor && assumption.evidenceFor.length > 0 && (
                        <div className="text-green-400/70">
                          <span className="font-medium">Supporting:</span> {assumption.evidenceFor.length} points
                        </div>
                      )}
                      {assumption.evidenceAgainst && assumption.evidenceAgainst.length > 0 && (
                        <div className="text-red-400/70">
                          <span className="font-medium">Challenging:</span> {assumption.evidenceAgainst.length} points
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {deliverable?.totalLatencySeconds && (
            <div className={cn('text-center text-text-secondary pt-3 border-t border-border/30', isSmallScreen ? 'text-xs' : 'text-sm')}>
              Analysis completed in {deliverable.totalLatencySeconds.toFixed(1)} seconds
            </div>
          )}
        </div>
      )}
    </div>
  );
}
